import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { endpoints, type DashboardSummaryResponse } from "@/lib/endpoints";
import { queryKeys } from "@/lib/query-keys";

/**
 * `GET /dashboard/summary` — everything the home screen shows (§11.2).
 *
 * ONE REQUEST FOR THE WHOLE SCREEN, deliberately. The alternative is four calls
 * (this month, total billed, waiting quotations, recent documents) and on a
 * workshop connection that is four chances to be slow and four spinners racing
 * each other into place. The endpoint exists precisely so the screen paints once.
 *
 * NO `select` OR CLIENT-SIDE ARITHMETIC ANYWHERE HERE. Every figure arrives
 * computed — §6 makes the server the only authority on totals, and a home screen
 * that summed its own paise would be a second implementation of the tax engine
 * waiting to disagree with the first.
 */
export function useDashboardSummary(): UseQueryResult<DashboardSummaryResponse> {
  return useQuery({
    queryKey: queryKeys.dashboard.summary(),
    queryFn: () => api.get<DashboardSummaryResponse>(endpoints.dashboardSummary),
    /*
     * Half a minute. The figures change only when the owner saves a document,
     * and every mutation already invalidates `queryKeys.dashboard.all` — so this
     * is not how the screen stays fresh, it is only how often a tab left open on
     * the office desktop re-checks by itself.
     */
    staleTime: 30_000,
  });
}
